// Search (v3 teardown).
//
// Live / Upcoming / Recent results for any team, league or country,
// rendered in flat hairline fixture rows with per-market side-chips.

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, Radio, Calendar, Archive, AlertCircle, Search as SearchIcon } from "lucide-react";
import { API } from "../App";
import TopNav from "../components/TopNav";
import SearchBar from "../components/SearchBar";
import { AddToSlipButton } from "../components/BetSlipDrawer";
import { countryToFlag } from "../lib/countryFlag";
import LiveOverlay from "../components/LiveOverlay";
import TeamLink from "../components/TeamLink";
import { useLiveSnapshot } from "../context/LiveSnapshotContext";
import {
  V3Page, V3Container, V3Header, V3Eyebrow,
  V3Tag, V3Num,
} from "../components/v3";

const formatKickoff = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const riskTone = (risk) => {
  const r = (risk || "").toLowerCase();
  if (r === "low") return "emerald";
  if (r === "medium") return "amber";
  if (r === "high") return "rose";
  if (r === "live") return "rose";
  return "mute";
};

const FixtureCard = ({ match, prediction, result, kind }) => {
  const navigate = useNavigate();
  const { isLive } = useLiveSnapshot();
  const home = match?.home_team?.name || "?";
  const away = match?.away_team?.name || "?";
  const league = match?.league?.name;
  const country = match?.league?.country;
  const flag = country ? countryToFlag(country) : "";
  const tps = prediction?.top_predictions || [];
  const fid = match?.fixture_id || match?.id;
  const liveNow = isLive(fid);
  const effectiveKind = kind === "upcoming" && liveNow ? "live" : kind;

  return (
    <article
      className="p-5 transition-colors"
      style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
      data-testid={`fixture-card-${fid}`}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div
          className="flex items-center gap-2 text-[11px] uppercase"
          style={{
            color: "var(--v3-text-3)",
            fontFamily: "var(--v3-font-mono)",
            letterSpacing: "0.12em",
          }}
        >
          {flag && <span aria-hidden>{flag}</span>}
          <span className="truncate max-w-[16rem]">{league || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          {effectiveKind === "live" && <LiveOverlay fixtureId={fid} />}
          {effectiveKind !== "live" && kind === "live" && (
            <V3Tag tone="rose">● LIVE · {match.status}</V3Tag>
          )}
          {kind === "recent" && result?.actual_score && (
            <V3Tag>
              FT {typeof result.actual_score === "string"
                ? result.actual_score
                : `${result.actual_score.home}-${result.actual_score.away}`}
            </V3Tag>
          )}
          <span
            className="text-[11px] inline-flex items-center gap-1"
            style={{
              color: "var(--v3-text-3)",
              fontFamily: "var(--v3-font-mono)",
            }}
          >
            <Clock className="w-3 h-3" strokeWidth={1.75} />
            {formatKickoff(match.date)}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => fid && navigate(`/match/${fid}`)}
        className="w-full text-left grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-5 group"
        data-testid={`fixture-open-${fid}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {match.home_team?.logo && (
            <img src={match.home_team.logo} alt="" className="w-7 h-7 object-contain shrink-0" />
          )}
          <span className="text-[15px] truncate group-hover:underline" style={{ fontWeight: 500 }}>
            <TeamLink name={home} onClick={(e) => e.stopPropagation()} />
          </span>
        </div>
        <span
          className="text-[10px] uppercase"
          style={{
            color: "var(--v3-text-4)",
            fontFamily: "var(--v3-font-mono)",
            letterSpacing: "0.16em",
          }}
        >
          VS
        </span>
        <div className="flex items-center gap-2 justify-end min-w-0">
          <span className="text-[15px] truncate text-right group-hover:underline" style={{ fontWeight: 500 }}>
            <TeamLink name={away} onClick={(e) => e.stopPropagation()} />
          </span>
          {match.away_team?.logo && (
            <img src={match.away_team.logo} alt="" className="w-7 h-7 object-contain shrink-0" />
          )}
        </div>
      </button>

      {tps.length > 0 ? (
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-0"
          style={{ borderTop: "1px solid var(--v3-line)" }}
        >
          {tps.slice(0, 8).map((p, i) => (
            <div
              key={i}
              className="px-3 py-2.5"
              style={{
                borderBottom: "1px solid var(--v3-line)",
                borderRight: i % 2 === 0 ? "1px solid var(--v3-line)" : "none",
              }}
              data-testid={`market-${fid}-${i}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span
                  className="text-[10px] uppercase truncate"
                  style={{
                    color: "var(--v3-text-3)",
                    fontFamily: "var(--v3-font-mono)",
                    letterSpacing: "0.14em",
                  }}
                >
                  {p.market_type}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <V3Tag tone={riskTone(p.risk_level)}>{p.risk_level}</V3Tag>
                  {kind !== "recent" && (
                    <AddToSlipButton
                      pick={{
                        fixture_id: fid,
                        home_team: home,
                        away_team: away,
                        league,
                        country,
                        kickoff: match.date,
                        market_type: p.market_type,
                        prediction: p.prediction,
                        confidence: p.confidence,
                        risk_level: p.risk_level,
                        odds: p.odds,
                        is_live: kind === "live",
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="flex items-end justify-between gap-2">
                <span className="text-[13px] truncate" style={{ fontWeight: 500 }}>{p.prediction}</span>
                <V3Num className="text-[14px]">{Math.round(p.confidence)}%</V3Num>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="px-3 py-4 text-center text-[12px]"
          style={{
            border: "1px dashed var(--v3-line-2)",
            background: "transparent",
            color: "var(--v3-text-3)",
          }}
        >
          No prediction compiled yet for this fixture.
          {fid && (
            <button
              onClick={() => navigate(`/match/${fid}`)}
              className="ml-2 underline-offset-4 hover:underline"
              style={{ color: "var(--v3-indigo-fg)", fontFamily: "var(--v3-font-mono)" }}
              data-testid={`open-match-${fid}`}
            >
              Open match
            </button>
          )}
        </div>
      )}
    </article>
  );
};

const Section = ({ title, icon: Icon, items, kind, emptyMsg }) => (
  <section className="mt-10 first:mt-0" data-testid={`section-${kind}`}>
    <div className="flex items-center justify-between mb-4">
      <div
        className="inline-flex items-center gap-2 text-[11px] uppercase"
        style={{
          color: "var(--v3-text-3)",
          fontFamily: "var(--v3-font-mono)",
          letterSpacing: "0.14em",
          fontWeight: 500,
        }}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
        {title}
      </div>
      <V3Num tone="var(--v3-text)" className="text-[12px]">{items.length}</V3Num>
    </div>
    <div style={{ borderTop: "1px solid var(--v3-line)" }} />
    {items.length === 0 ? (
      <div
        className="mt-4 px-4 py-6 text-sm text-center"
        style={{
          border: "1px dashed var(--v3-line-2)",
          color: "var(--v3-text-3)",
        }}
      >
        {emptyMsg}
      </div>
    ) : (
      <div className="grid gap-3 mt-4">
        {items.map((m, i) => (
          <FixtureCard
            key={(m.fixture_id || m.id || i) + "-" + kind}
            match={m}
            prediction={m.prediction}
            result={m.result}
            kind={kind}
          />
        ))}
      </div>
    )}
  </section>
);

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!q.trim()) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    const ctrl = new AbortController();
    axios
      .get(`${API}/search/predictions`, {
        params: { q, days_back: 7, days_ahead: 30, limit: 40 },
        signal: ctrl.signal,
      })
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.name !== "CanceledError") setError(e.message || "Search failed");
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [q]);

  return (
    <V3Page testid="search-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="search-header"
          eyebrow={<V3Eyebrow icon={SearchIcon}>Fixture search</V3Eyebrow>}
          title="Predict any team."
          titleAccent="Live · upcoming · recent — on demand."
          subtitle="Find live, upcoming, and recent matches by team, league or country — including fixtures that aren't on today's top 25."
        />

        <div className="max-w-xl mb-10">
          <SearchBar initialQuery={q} autoFocus={!q} />
        </div>

        {!q.trim() && (
          <div
            className="px-6 py-10 text-center"
            style={{ border: "1px dashed var(--v3-line-2)", color: "var(--v3-text-3)" }}
            data-testid="search-empty-state"
          >
            <p className="text-[13px]">
              Try searching{" "}
              <span style={{ color: "var(--v3-text)", fontWeight: 500 }}>Arsenal</span>,{" "}
              <span style={{ color: "var(--v3-text)", fontWeight: 500 }}>Premier League</span>, or{" "}
              <span style={{ color: "var(--v3-text)", fontWeight: 500 }}>Brazil</span>.
            </p>
          </div>
        )}

        {loading && (
          <div
            className="px-6 py-10 text-center text-[13px]"
            style={{
              border: "1px solid var(--v3-line)",
              background: "var(--v3-surface)",
              color: "var(--v3-text-3)",
              fontFamily: "var(--v3-font-mono)",
            }}
            data-testid="search-loading"
          >
            Searching fixtures…
          </div>
        )}

        {error && (
          <div
            className="px-4 py-4 text-[13px] flex items-center gap-2"
            style={{
              border: "1px solid rgba(244,63,94,0.35)",
              background: "rgba(244,63,94,0.08)",
              color: "var(--v3-rose, #fda4af)",
            }}
            data-testid="search-error"
          >
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {data && !loading && (
          <>
            <div
              className="text-[12px] mb-6"
              style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
              data-testid="search-result-count"
            >
              <V3Num>{data.counts?.total ?? 0}</V3Num> fixtures matched
              {" "}
              <span style={{ color: "var(--v3-text)" }}>"{q}"</span>
              {" "}
              <span style={{ color: "var(--v3-text-4)" }}>· last 7 days + next 30 days</span>
            </div>

            <Section
              title="Live now"
              icon={Radio}
              items={data.live || []}
              kind="live"
              emptyMsg="No live matches for this query right now."
            />

            <Section
              title="Upcoming fixtures"
              icon={Calendar}
              items={data.upcoming || []}
              kind="upcoming"
              emptyMsg="No upcoming fixtures in the next 30 days. Try a different team or league."
            />

            <Section
              title="Recent results"
              icon={Archive}
              items={data.recent || []}
              kind="recent"
              emptyMsg="No matches finished in the last 7 days for this query."
            />
          </>
        )}
      </V3Container>
    </V3Page>
  );
}
